import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { Menu, X, ArrowUpRight } from "lucide-react";

const Navbar = () => {
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const items = [
    { to: "/", label: t("nav.home"), testid: "nav-home" },
    { to: "/services", label: t("nav.services"), testid: "nav-services" },
    { to: "/about", label: t("nav.about"), testid: "nav-about" },
    { to: "/contact", label: t("nav.contact"), testid: "nav-contact" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-glass" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 py-3.5 md:py-5 flex items-center justify-between gap-3">
        {/* Logo */}
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="logo-link"
        >
          <div className="w-9 h-9 bg-[#E5FF00] flex items-center justify-center text-black font-display text-lg leading-none">
            T
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-lg tracking-tight">
              Terwilliger
            </span>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
              s.r.o. · Praha
            </span>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/"}
              data-testid={it.testid}
              className={({ isActive }) =>
                `link-underline font-mono-tech text-sm uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-[#E5FF00] active" : "text-white hover:text-[#E5FF00]"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3 md:gap-5 lg:gap-6">
          {/* Lang switcher */}
          <div
            className="flex items-center gap-1 font-mono-tech text-xs uppercase tracking-[0.2em]"
            data-testid="lang-switcher"
          >
            <button
              onClick={() => setLang("cs")}
              data-testid="lang-cs-btn"
              className={`px-2 py-2 min-w-[40px] min-h-[40px] flex items-center justify-center transition-colors ${
                lang === "cs" ? "text-[#E5FF00]" : "text-zinc-500 hover:text-white"
              }`}
            >
              CS
            </button>
            <span className="text-zinc-700">/</span>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en-btn"
              className={`px-2 py-2 min-w-[40px] min-h-[40px] flex items-center justify-center transition-colors ${
                lang === "en" ? "text-[#E5FF00]" : "text-zinc-500 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          {/* CTA */}
          <Link
            to="/contact"
            className="hidden xl:inline-flex btn-acid"
            data-testid="nav-cta-btn"
          >
            {t("nav.cta")}
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </Link>

          {/* Mobile menu */}
          <button
            className="lg:hidden text-white p-2 -mr-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden bg-black border-t border-white/10"
          data-testid="mobile-menu-drawer"
        >
          <nav className="flex flex-col p-6 gap-2">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={`${it.testid}-mobile`}
                className={({ isActive }) =>
                  `font-display text-2xl sm:text-3xl py-3 border-b border-white/5 ${
                    isActive ? "text-[#E5FF00]" : "text-white"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="btn-acid mt-6 justify-center"
              data-testid="nav-cta-btn-mobile"
            >
              {t("nav.cta")}
              <ArrowUpRight size={16} strokeWidth={2.5} />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
